import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import type { KhaataCategory, KhaataCategoryFormData } from '../../../types/khaataCategory';
import type { CategoryOption, KhaataEntryType } from '../../../constants/khaataCategories';
import {
  KHAATA_TYPES,
  DEFAULT_CATEGORIES,
  buildCategoryOptions,
  getCategoriesForType,
  getTypeLabel,
  isCategoryEnabled,
  slugifyCategory,
} from '../../../constants/khaataCategories';
import {
  getKhaataCategories,
  addKhaataCategory,
  updateKhaataCategory,
  deleteKhaataCategory,
  setCategoryEnabled,
} from '@utilities/khaataCategoryService';
import { ConfirmationModal } from '@components/ConfirmModal//Confirmation';

interface CategoriesManagerProps {
  open: boolean;
  restId: string;
  onClose: () => void;
  onCategoriesChange?: () => void;
}

const emptyForm = (restId: string): KhaataCategoryFormData => ({
  restId,
  name: '',
  value: '',
  type: 'expense',
  enabled: true,
});

const TYPE_COLORS: Record<KhaataEntryType, 'primary' | 'secondary' | 'info' | 'warning'> = {
  expense: 'primary',
  purchase: 'secondary',
  asset: 'info',
  liability: 'warning',
};

const CategoriesManager: React.FC<CategoriesManagerProps> = ({
  open,
  restId,
  onClose,
  onCategoriesChange,
}) => {
  const [custom, setCustom] = useState<KhaataCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<KhaataCategory | null>(null);
  const [formData, setFormData] = useState<KhaataCategoryFormData>(emptyForm(restId));
  const [isSaving, setIsSaving] = useState(false);
  const [togglingValue, setTogglingValue] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KhaataCategory | null>(null);

  const loadCategories = useCallback(async () => {
    if (!restId) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await getKhaataCategories(restId);
      setCustom(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  }, [restId]);

  useEffect(() => {
    if (open && restId) loadCategories();
  }, [open, restId, loadCategories]);

  const customAsOptions: CategoryOption[] = useMemo(
    () =>
      custom.map((c) => ({
        value: c.value,
        label: c.name,
        type: c.type,
        enabled: c.enabled,
      })),
    [custom]
  );

  /** Full grouped view: defaults + custom, grouped by type, sorted alphabetically. */
  const grouped = useMemo(() => {
    const all = buildCategoryOptions(customAsOptions);
    const customByValue = new Map(custom.map((c) => [c.value, c]));
    const defaultValues = new Set(DEFAULT_CATEGORIES.map((c) => c.value));
    const byType: Record<KhaataEntryType, { option: CategoryOption; custom?: KhaataCategory }[]> = {
      expense: [],
      purchase: [],
      asset: [],
      liability: [],
    };

    KHAATA_TYPES.forEach((typeDef) => {
      const items = getCategoriesForType(typeDef.value, all);
      items.forEach((option) => {
        const customMatch = customByValue.get(option.value);
        byType[typeDef.value].push({
          option: {
            ...option,
            isDefault: defaultValues.has(option.value),
          },
          custom: customMatch,
        });
      });
    });

    return byType;
  }, [custom, customAsOptions]);

  const handleOpenAdd = () => {
    setEditing(null);
    setFormData(emptyForm(restId));
    setFormOpen(true);
  };

  const handleOpenEdit = (category: KhaataCategory) => {
    setEditing(category);
    setFormData({
      restId: category.restId,
      name: category.name,
      value: category.value,
      type: category.type,
      enabled: category.enabled,
    });
    setFormOpen(true);
  };

  const handleToggleEnabled = async (
    option: CategoryOption,
    customCat: KhaataCategory | undefined,
    nextEnabled: boolean
  ) => {
    const optimisticId = `optimistic-${option.value}`;
    const previousCustom = custom;

    setCustom((prev) => {
      if (customCat) {
        return prev.map((c) =>
          c.id === customCat.id ? { ...c, enabled: nextEnabled } : c
        );
      }

      return [
        ...prev,
        {
          id: optimisticId,
          restId,
          name: option.label,
          value: option.value,
          type: option.type,
          enabled: nextEnabled,
        },
      ];
    });

    try {
      setTogglingValue(option.value);
      setError(null);
      const saved = await setCategoryEnabled(restId, option, nextEnabled, customCat);
      setCustom((prev) => {
        const withoutOptimistic = prev.filter((c) => c.id !== optimisticId);
        const exists = withoutOptimistic.some((c) => c.id === saved.id);
        if (exists) {
          return withoutOptimistic.map((c) => (c.id === saved.id ? saved : c));
        }
        return [...withoutOptimistic, saved];
      });
      onCategoriesChange?.();
    } catch (err) {
      setCustom(previousCustom);
      setError(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setTogglingValue(null);
    }
  };

  const handleSave = async () => {
    const name = formData.name.trim();
    if (!name) {
      setError('Category name is required');
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      const payload: KhaataCategoryFormData = {
        ...formData,
        name,
        value: editing ? formData.value : slugifyCategory(name),
        enabled: formData.enabled !== false,
      };
      if (editing) {
        await updateKhaataCategory(editing.id, payload);
      } else {
        await addKhaataCategory(payload);
      }
      setFormOpen(false);
      await loadCategories();
      onCategoriesChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setError(null);
      await deleteKhaataCategory(deleteTarget.id);
      setDeleteTarget(null);
      await loadCategories();
      onCategoriesChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Manage Categories
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Toggle off categories you don&apos;t need in Add Entry. Disabled ones stay hidden from receptionists.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 28 }} />}
              onClick={handleOpenAdd}
              sx={{
                px: 4,
                py: 1.5,
                minHeight: 56,
                fontSize: '1.125rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Add
            </Button>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            KHAATA_TYPES.map((typeDef) => {
              const items = grouped[typeDef.value];
              if (!items.length) return null;
              return (
                <Box key={typeDef.value} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      label={typeDef.label}
                      size="small"
                      color={TYPE_COLORS[typeDef.value]}
                      variant="filled"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {typeDef.hint}
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {items.map(({ option, custom: customCat }, index) => {
                      const enabled = isCategoryEnabled(option);
                      return (
                        <React.Fragment key={option.value}>
                          {index > 0 && <Divider component="li" />}
                          <ListItem
                            sx={{
                              opacity: enabled ? 1 : 0.55,
                              pr: { xs: 14, sm: 18 },
                            }}
                            secondaryAction={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Switch
                                  size="small"
                                  checked={enabled}
                                  disabled={togglingValue === option.value}
                                  onChange={(e) =>
                                    handleToggleEnabled(option, customCat, e.target.checked)
                                  }
                                  inputProps={{
                                    'aria-label': `${enabled ? 'Disable' : 'Enable'} ${option.label}`,
                                  }}
                                />
                                {customCat && !option.isDefault ? (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenEdit(customCat)}
                                      aria-label="Edit"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setDeleteTarget(customCat)}
                                      aria-label="Delete"
                                    >
                                      <DeleteForeverIcon fontSize="small" />
                                    </IconButton>
                                  </>
                                ) : option.isDefault ? (
                                  <Chip
                                    label="Default"
                                    size="small"
                                    variant="outlined"
                                    sx={{ opacity: 0.6, ml: 0.5 }}
                                  />
                                ) : null}
                              </Box>
                            }
                          >
                            <ListItemText
                              primary={option.label}
                              secondary={enabled ? undefined : 'Hidden from Add Entry'}
                            />
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            autoFocus
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="category-type-label">Type</InputLabel>
            <Select
              labelId="category-type-label"
              value={formData.type}
              label="Type"
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as KhaataEntryType })
              }
            >
              {KHAATA_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {getTypeLabel(t.value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled !== false}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
            }
            label="Show in Add Entry"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={!!deleteTarget}
        title="Delete Category"
        message={`Delete "${deleteTarget?.name}"? Existing entries keep their saved category label.`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        severity="error"
      />
    </>
  );
};

export default CategoriesManager;
